(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const VIDEO_PLAYBACK_RATE = 0.62;

  function initPopularProductCards(scope) {
    const root = scope || document;
    const forms = root.querySelectorAll('.bamboo-product-card__form');

    forms.forEach((form) => {
      if (form.dataset.bambooPopularBound === 'true') return;
      form.dataset.bambooPopularBound = 'true';

      form.addEventListener('submit', () => {
        const button = form.querySelector('.bamboo-product-card__add');
        if (!button) return;

        button.classList.add('is-adding');
        window.setTimeout(() => button.classList.remove('is-adding'), 600);
      });
    });
  }

  function initScrollReveal(section) {
    const revealTargets = section.querySelectorAll('[data-bamboo-reveal]');
    if (!revealTargets.length) return;

    if (REDUCED_MOTION || !('IntersectionObserver' in window)) {
      revealTargets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    revealTargets.forEach((el) => observer.observe(el));
  }

  function initParallax(section) {
    const parallaxItems = section.querySelectorAll('[data-bamboo-parallax]');
    if (!parallaxItems.length || REDUCED_MOTION) return;

    let ticking = false;

    function updateParallax() {
      const sectionRect = section.getBoundingClientRect();
      const viewHeight = window.innerHeight || 1;
      const progress =
        1 - (sectionRect.top + sectionRect.height * 0.5) / (viewHeight + sectionRect.height * 0.5);
      const clamped = Math.max(-1, Math.min(1, progress));

      parallaxItems.forEach((item) => {
        const strength = parseFloat(item.dataset.bambooParallax || '0.1');
        const offset = clamped * strength * 20;
        item.style.setProperty('--parallax-y', `${offset}px`);
      });

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateParallax);
    }

    updateParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }

  function initWindVideo(section) {
    const stack = section.querySelector('[data-bamboo-wind-stack]');
    const scene = stack ? stack.closest('.bamboo-wind-scene') : null;
    const videos = stack ? Array.from(stack.querySelectorAll('[data-bamboo-wind-video]')) : [];
    const singleVideo = section.querySelector('[data-bamboo-wind-video]');
    const sceneRoot = scene || (singleVideo ? singleVideo.closest('.bamboo-wind-scene') : null);

    if (!sceneRoot) return;

    const CROSSFADE_SEC = 0.65;
    let activeIndex = 0;
    let crossfading = false;

    function syncBlendMode(video) {
      const src = video.currentSrc || '';
      sceneRoot.classList.toggle('bamboo-wind-scene--opaque-fallback', src.includes('.mp4'));
    }

    function setVideoOpacity(video, value) {
      video.style.opacity = String(value);
    }

    function warmVideos() {
      videos.forEach((video) => {
        if (video.preload !== 'auto') video.preload = 'auto';
      });
    }

    function primeVideo(video) {
      if (video.readyState >= 2) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          video.removeEventListener('loadeddata', done);
          video.removeEventListener('canplay', done);
          resolve();
        };

        video.addEventListener('loadeddata', done);
        video.addEventListener('canplay', done);
        if (video.preload !== 'auto') video.preload = 'auto';
      });
    }

    function applyPlaybackRate(video) {
      video.playbackRate = VIDEO_PLAYBACK_RATE;
      video.defaultPlaybackRate = VIDEO_PLAYBACK_RATE;
    }

    function playVideo(video) {
      if (video.preload === 'none') video.preload = 'auto';
      applyPlaybackRate(video);
      return video.play().catch(() => {});
    }

    function pauseAll() {
      videos.forEach((video) => video.pause());
      sceneRoot.classList.remove('is-playing');
    }

    function crossfadeToNext() {
      if (videos.length < 2 || crossfading) return;

      const current = videos[activeIndex];
      const nextIndex = activeIndex === 0 ? 1 : 0;
      const next = videos[nextIndex];
      crossfading = true;

      next.currentTime = 0;

      const beginFade = () => {
        playVideo(next);
        const start = performance.now();
        const durationMs = CROSSFADE_SEC * 1000;

        function frame(now) {
          const t = Math.min(1, (now - start) / durationMs);
          const ease = t * t * (3 - 2 * t);
          setVideoOpacity(current, 1 - ease);
          setVideoOpacity(next, ease);

          if (t < 1) {
            requestAnimationFrame(frame);
            return;
          }

          current.pause();
          current.currentTime = 0;
          setVideoOpacity(current, 0);
          setVideoOpacity(next, 1);
          current.classList.remove('is-active');
          next.classList.add('is-active');
          activeIndex = nextIndex;
          crossfading = false;
        }

        requestAnimationFrame(frame);
      };

      primeVideo(next).then(beginFade);
    }

    function bindCrossfadeLoop(video, index) {
      video.addEventListener('loadeddata', () => syncBlendMode(video));

      video.addEventListener('timeupdate', () => {
        if (index !== activeIndex || crossfading || !video.duration) return;
        if (video.currentTime >= video.duration - CROSSFADE_SEC) {
          crossfadeToNext();
        }
      });
    }

    if (videos.length >= 2) {
      videos.forEach((video, index) => {
        applyPlaybackRate(video);
        setVideoOpacity(video, index === 0 ? 1 : 0);
        bindCrossfadeLoop(video, index);
      });
      syncBlendMode(videos[0]);
    } else if (singleVideo) {
      singleVideo.addEventListener('loadeddata', () => syncBlendMode(singleVideo));
      syncBlendMode(singleVideo);
    }

    if (REDUCED_MOTION) return;

    const playTargets = videos.length ? videos : singleVideo ? [singleVideo] : [];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            warmVideos();
            const active = playTargets[activeIndex] || playTargets[0];
            if (active) {
              Promise.all(videos.map((video) => primeVideo(video)))
                .then(() => playVideo(active))
                .then(() => sceneRoot.classList.add('is-playing'));
            }
          } else {
            pauseAll();
          }
        });
      },
      { threshold: 0.08 }
    );

    observer.observe(section);
  }

  function initSection(root) {
    const scope = root || document;
    const section = scope.querySelector('[data-bamboo-popular-products]');
    if (!section || section.dataset.bambooPopularInit === 'true') return;

    section.dataset.bambooPopularInit = 'true';
    initPopularProductCards(section);
    initScrollReveal(section);
    initParallax(section);
    initWindVideo(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSection());
  } else {
    initSection();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSection(event.target);
  });
})();
